import { useCallback, useMemo, useState } from 'react';
import {
  Alert, AlertTitle, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  MenuItem, Stack, TextField, Typography,
} from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid';
import { useQuery } from '@tanstack/react-query';
import { usePageTitle } from '../../components/shell/AppShell';
import { RouteDataGrid } from '../../components/data-grid/RouteDataGrid';
import type { QuickFilter, RowAction, SearchField } from '../../components/data-grid/RouteDataGrid';
import { EntityDetailDialog, auditTrailFields } from '../../components/dialogs/EntityDetailDialog';
import { TerminalDialog } from './TerminalDialog';
import type { DetailRow } from '../../components/dialogs/EntityDetailDialog';
import { SiteControllerHeartbeatPanel } from './SiteControllerHeartbeatPanel';
import { fmtDateTime, fmtOrEmDash } from '../../utils/format';
import type { ExportColumn } from '../../utils/export';
import type { Operator, SiteControllerSummary } from '../../api/types';
import {
  useApproveVoid,
  useApproveVoidReversal,
  useCancelPendingVoid,
  useCancelPendingVoidReversal,
  useConsoleBreakGlass,
  useMe,
  useMyPermissions,
  useOperatorsList,
  useRequestVoid,
  useRequestVoidReversal,
  useScLifecycleAction,
  useScRestartAction,
  useSiteControllersList,
} from '../../api/queries';
import { useAlert, useConfirm } from '../../components/dialogs/DialogProvider';

/**
 * Site controllers — consolidated operator-aware commissioning + ops surface.
 *
 * V2 lifecycle: ready_for_install → commissioning → installing → in_service.
 * Plus terminal: voided / defective / decommissioning / retired.
 *
 * This page now lives in the operator-visible Operations menu (the old
 * heartbeat-only "Fleet" page was folded in). Audience split:
 *   - Field-tech mutations (activate, mark-defective) go through the
 *     mobile app, not this portal.
 *   - Operators self-serve the FULL set of actions on the SCs commissioned
 *     to their operator — incl. the destructive void / reversal flow —
 *     gated by their held permissions (`has('provisioning.site-controllers.*')`)
 *     and enforced server-side by the endpoint ownership guard.
 *   - ARMS staff act on every SC incl. the uncommissioned / shipping pool.
 *
 * Operator-tenant users see only SCs commissioned to their operator
 * (via RLS in 0112_rls_provisioning_v2.sql) — the uncommissioned pool +
 * voided rows are ARMS-Staff only.
 */
export default function SiteControllersPage() {
  usePageTitle('Site controllers');
  const me = useMe();
  const isArms = me.data?.is_arms ?? false;
  const perms = useMyPermissions();
  const has = useCallback(
    (code: string) => perms.data?.codes?.includes(code) ?? false,
    [perms.data?.codes],
  );
  const confirm = useConfirm();
  const alert = useAlert();
  const restart = useScRestartAction();
  const doRestart = useCallback(async (
    row: SiteControllerSummary, action: 'restart-services' | 'reboot',
  ) => {
    const reboot = action === 'reboot';
    const label = row.siteDisplayName ?? row.qrId ?? row.id;
    const ok = await confirm({
      title: reboot ? 'Reboot site controller' : 'Restart site-controller services',
      message: reboot
        ? <>Fully reboot <strong>{label}</strong>? It goes offline for 1–2 minutes; all connected
            machines drop until it returns.</>
        : <>Restart the ARMS agent services on <strong>{label}</strong>? Brief interruption; no full
            reboot. Machines reconnect within seconds.</>,
      confirmLabel: reboot ? 'Reboot' : 'Restart services',
      destructive: true,
    });
    if (!ok) return;
    try {
      const r = await restart.mutateAsync({ scId: row.id, action });
      await alert({ title: 'Restart requested', message: r.message, severity: 'success' });
    } catch (e) {
      await alert({ title: 'Restart failed',
        message: (e as { message?: string }).message ?? 'Could not queue the restart.', severity: 'error' });
    }
  }, [confirm, alert, restart]);

  const [operatorFilter, setOperatorFilter] = useState<string>('');
  const operatorIdForList = isArms ? (operatorFilter || null) : (me.data?.operator_id ?? null);
  const { queryKey, listFn } = useSiteControllersList({ operatorId: operatorIdForList });

  const operators = useOperatorsList();
  const operatorsQ = useQuery({
    queryKey: operators.queryKey,
    queryFn: operators.listFn,
    enabled: isArms,
  });
  const operatorById = useMemo(() => {
    const m = new Map<string, Operator>();
    for (const o of operatorsQ.data ?? []) m.set(o.id, o);
    return m;
  }, [operatorsQ.data]);

  const [detailRow, setDetailRow] = useState<SiteControllerSummary | null>(null);
  const [voidTarget, setVoidTarget] = useState<SiteControllerSummary | null>(null);
  const [approveVoidTarget, setApproveVoidTarget] = useState<SiteControllerSummary | null>(null);
  const [reverseVoidTarget, setReverseVoidTarget] = useState<SiteControllerSummary | null>(null);
  const [approveReversalTarget, setApproveReversalTarget] = useState<SiteControllerSummary | null>(null);
  const [lifecycleTarget, setLifecycleTarget] = useState<{
    sc: SiteControllerSummary;
    action: 'decommission' | 'recommission';
  } | null>(null);
  const [terminalTarget, setTerminalTarget] = useState<SiteControllerSummary | null>(null);

  const columns = useMemo<GridColDef<SiteControllerSummary>[]>(() => [
    { field: 'qrId', headerName: 'QR Code', width: 240,
      // V2 chassis QR Code — single QR per device. Persists across
      // the whole lifecycle (ready_for_install → in_service).
      // Shown monospace so techs can compare against the printed label.
      renderCell: (p) => p.row.qrId
        ? <code style={{ fontSize: 11 }}>{p.row.qrId}</code>
        : <Typography variant="body2" color="text.secondary">— (no QR Code)</Typography> },
    { field: 'siteDisplayName', headerName: 'Site', flex: 1.4,
      renderCell: (p) => p.row.siteDisplayName
        ? <Typography variant="body2">{p.row.siteDisplayName}</Typography>
        : <Typography variant="body2" color="text.secondary">— (uncommissioned)</Typography> },
    ...(isArms ? [{
      field: 'intendedOperatorId', headerName: 'Operator', width: 220,
      renderCell: (p: { row: SiteControllerSummary }) => {
        if (!p.row.intendedOperatorId) {
          return <Typography variant="body2" color="text.secondary">— (uncommissioned pool)</Typography>;
        }
        const op = operatorById.get(p.row.intendedOperatorId);
        return op ? `${op.displayName} (${op.code})` : <em>operator not visible</em>;
      },
    } as GridColDef<SiteControllerSummary>] : []),
    { field: 'status', headerName: 'Status', width: 260,
      renderCell: (p) => (
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
          <StatusChip status={p.row.status} />
          {p.row.pendingVoidRequestedAt && p.row.status !== 'voided' && (
            <Chip size="small" label="void pending" color="warning" />
          )}
          <CertLifecycleChips
            renewalOverdue={p.row.certRenewalOverdue}
            expiredOffline={p.row.certExpiredOffline}
          />
        </Stack>
      ) },
    // SC OS Version prefers the live image version reported in the most-recent
    // heartbeat (issue #29); `firmwareVersion` (legacy schema name, baked at
    // commission) is the fallback for SCs whose runtime predates the change.
    // `runtimeVersion` is the live sc-runtime version executing today.
    { field: 'firmwareVersion', headerName: 'SC OS Version', width: 150,
      renderCell: (p) => {
        const v = p.row.imageVersion ?? p.row.firmwareVersion;
        return v
          ? <code>{v}</code>
          : <Typography variant="body2" color="text.secondary">—</Typography>;
      } },
    { field: 'runtimeVersion', headerName: 'SC Runtime', width: 130,
      renderCell: (p) => p.row.runtimeVersion
        ? <code>{p.row.runtimeVersion}</code>
        : <Typography variant="body2" color="text.secondary">—</Typography> },
    // Installed bootstrap (sc-app) version from the latest heartbeat.
    { field: 'bootstrapVersion', headerName: 'SC App', width: 120,
      renderCell: (p) => p.row.bootstrapVersion
        ? <code>{p.row.bootstrapVersion}</code>
        : <Typography variant="body2" color="text.secondary">—</Typography> },
    // Board self-identity — family + measured eMMC from the board_type
    // catalogue (migration 0378). Null until the SC runs a board-reporting runtime.
    { field: 'boardFamily', headerName: 'Board', width: 120,
      renderCell: (p) => {
        const fam = p.row.boardFamily;
        if (!fam) return <Typography variant="body2" color="text.secondary">—</Typography>;
        const emmc = p.row.emmcTotalBytes;
        const disk = emmc ? ` · ${Math.round(emmc / 1e9)}GB` : '';
        return <Typography variant="body2">{fam.toUpperCase()}{disk}</Typography>;
      } },
    { field: 'lastHeartbeatAt', headerName: 'Last seen', width: 180,
      valueFormatter: (value: unknown) => value ? fmtDateTime(value) : '—' },
    { field: 'deviceCertExpiry', headerName: 'Cert expiry', width: 200,
      renderCell: (p) => <CertExpiryCell expiry={p.row.deviceCertExpiry} /> },
    { field: 'createdAt', headerName: 'Registered', width: 180,
      valueFormatter: (value: unknown) => fmtDateTime(value) },
  ], [isArms, operatorById]);

  const searchFields = useMemo<SearchField[]>(() => [
    { kind: 'text',   key: 'qrId', label: 'QR Code' },
    { kind: 'text',   key: 'siteDisplayName', label: 'Site name' },
    { kind: 'text',   key: 'firmwareVersion', label: 'SC OS Version' },
    { kind: 'text',   key: 'runtimeVersion',  label: 'SC Runtime' },
    { kind: 'text',   key: 'bootstrapVersion', label: 'SC App' },
    { kind: 'select', key: 'status', label: 'Status', options: STATUS_OPTIONS },
  ], []);

  // V2 quick filters. "Pending void" is available to everyone — operators
  // now self-serve voids on their OWN SCs, so their own pending voids are
  // visible + actionable. The "Uncommissioned" pool view + the "Voided"
  // filter stay ARMS-Staff only: operators can't see uncommissioned or
  // voided rows at all (RLS hides them), so those filters would be dead.
  const quickFilters = useMemo<QuickFilter<SiteControllerSummary>[]>(() => {
    const base: QuickFilter<SiteControllerSummary>[] = [
      { label: 'All',           filter: () => true },
      { label: 'In service',    filter: (r) => r.status === 'in_service' },
      { label: 'Commissioning', filter: (r) => r.status === 'commissioning' || r.status === 'installing' },
      { label: 'Defective',     filter: (r) => r.status === 'defective' },
      { label: 'Pending void',  filter: (r) => !!r.pendingVoidRequestedAt && r.status !== 'voided' },
    ];
    if (isArms) {
      base.splice(1, 0, {
        label: 'Uncommissioned',
        filter: (r) => r.status === 'ready_for_install',
      });
      base.push({ label: 'Voided', filter: (r) => r.status === 'voided' });
    }
    return base;
  }, [isArms]);

  const exportColumns = useMemo<ExportColumn<SiteControllerSummary>[]>(() => [
    // QR Code is the human-readable identity — never export the raw id GUID
    // (PORTAL_FORM_RULES: no raw GUIDs), now that operators can export too.
    { key: 'qrId',                   label: 'QR Code',             format: (v) => fmtOrEmDash(v) },
    { key: 'siteDisplayName',        label: 'Site',                format: (v) => fmtOrEmDash(v) },
    // Operator column is ARMS-only (operators only ever see their own SCs, and
    // the operator lookup isn't loaded for them) — mirrors the grid column.
    ...(isArms ? [{ key: 'intendedOperatorId' as const, label: 'Operator',
      format: (v: unknown) => v
        ? (operatorById.get(String(v))?.displayName ?? '— (operator not visible)')
        : '— (uncommissioned)' }] : []),
    { key: 'status',                 label: 'Status' },
    { key: 'firmwareVersion',        label: 'SC OS Version',       format: (v) => fmtOrEmDash(v) },
    { key: 'runtimeVersion',         label: 'SC Runtime',          format: (v) => fmtOrEmDash(v) },
    { key: 'bootstrapVersion',       label: 'SC App',              format: (v) => fmtOrEmDash(v) },
    { key: 'lastHeartbeatAt',        label: 'Last seen',           format: (v) => v ? fmtDateTime(v) : '—' },
    { key: 'deviceCertExpiry',       label: 'Cert expiry',         format: (v) => v ? fmtDateTime(v) : '—' },
    { key: 'certRenewalOverdue',     label: 'Cert renewal overdue', format: (v) => v ? 'yes' : 'no' },
    { key: 'certExpiredOffline',     label: 'Cert expired offline', format: (v) => v ? 'yes' : 'no' },
    { key: 'commissionedAt',         label: 'Commissioned at',     format: (v) => v ? fmtDateTime(v) : '—' },
    { key: 'commissioningTechName',  label: 'Commissioning tech',  format: (v) => fmtOrEmDash(v) },
    { key: 'voidedAt',               label: 'Voided at',           format: (v) => v ? fmtDateTime(v) : '—' },
    { key: 'voidReason',             label: 'Void reason',         format: (v) => fmtOrEmDash(v) },
    { key: 'createdAt',              label: 'Registered',          format: (v) => fmtDateTime(v) },
  ], [operatorById, isArms]);

  // Row actions are stratified by V2 §16: void requires ARMS-Staff,
  // is gated by separate request + approve permissions, and approve
  // is hidden from the original requester (server-side check; we don't
  // know who requested without an extra round-trip, so the API enforces).
  const actions = useMemo<RowAction<SiteControllerSummary>[]>(() => [
    { label: 'View', onClick: (row) => setDetailRow(row) },
    {
      label: 'Request void',
      destructive: true,
      visible: (row) => has('provisioning.site-controllers.void')
        && !row.pendingVoidRequestedAt
        && !['voided', 'retired', 'in_service', 'decommissioning'].includes(row.status),
      onClick: (row) => setVoidTarget(row),
    },
    {
      label: 'Approve void',
      destructive: true,
      visible: (row) => has('provisioning.site-controllers.void-approve')
        && !!row.pendingVoidRequestedAt
        && row.status !== 'voided',
      onClick: (row) => setApproveVoidTarget(row),
    },
    {
      label: 'Request void reversal',
      visible: (row) => has('provisioning.site-controllers.void-reverse')
        && row.status === 'voided',
      onClick: (row) => setReverseVoidTarget(row),
    },
    {
      label: 'Approve void reversal',
      visible: (row) => has('provisioning.site-controllers.void-reverse-approve')
        && row.status === 'voided',
      onClick: (row) => setApproveReversalTarget(row),
    },
    {
      label: 'Decommission',
      destructive: true,
      visible: (row) => has('provisioning.site-controllers.admin')
        && ['in_service', 'installing', 'defective'].includes(row.status),
      onClick: (row) => setLifecycleTarget({ sc: row, action: 'decommission' }),
    },
    {
      label: 'Recommission',
      visible: (row) => has('provisioning.site-controllers.admin')
        && row.status === 'decommissioning',
      onClick: (row) => setLifecycleTarget({ sc: row, action: 'recommission' }),
    },
    {
      label: 'Open remote terminal',
      visible: (row) => has('provisioning.site-controllers.terminal')
        && row.status === 'in_service',
      onClick: (row) => setTerminalTarget(row),
    },
    {
      label: 'Restart services',
      visible: (row) => has('provisioning.site-controllers.restart-services')
        && row.status === 'in_service',
      onClick: (row) => doRestart(row, 'restart-services'),
    },
    {
      label: 'Reboot',
      destructive: true,
      visible: (row) => has('provisioning.site-controllers.reboot')
        && row.status === 'in_service',
      onClick: (row) => doRestart(row, 'reboot'),
    },
  ], [has, doRestart]);

  // V2 detail block — preserves the audit-trail block per
  // PORTAL_FORM_RULES.md and adds the V2 void/reversal actor names.
  const detailFields = useMemo<DetailRow<SiteControllerSummary>[]>(() => [
    { label: 'QR Code',
      render: (r) => r.qrId ? <code>{r.qrId}</code> : '— (no QR Code)' },
    { label: 'Site',
      render: (r) => r.siteDisplayName ?? '— (uncommissioned)' },
    { label: 'Operator',
      render: (r) => {
        if (!r.intendedOperatorId) return '— (uncommissioned pool)';
        const op = operatorById.get(r.intendedOperatorId);
        return op ? `${op.displayName} (${op.code})` : '— (operator not visible)';
      } },
    { label: 'Status',          render: (r) => <StatusChip status={r.status} /> },
    // SC OS Version prefers the live image version from the latest heartbeat
    // (issue #29); `firmwareVersion` (legacy schema name, set at commission) is
    // the fallback for SCs whose runtime predates the heartbeat field.
    { label: 'SC OS Version',
      render: (r) => {
        const v = r.imageVersion ?? r.firmwareVersion;
        return v ? <code>{v}</code> : '—';
      } },
    // Issue #29 — running-OS PRETTY_NAME from the latest heartbeat
    // (/etc/os-release). `—` until a new heartbeat arrives from an
    // updated runtime.
    { label: 'Running OS',
      render: (r) => fmtOrEmDash(r.osPrettyName) },
    // Live sc-runtime version from the most-recent heartbeat (resolved by
    // FetchLatestHeartbeatsAsync on the List endpoint).
    { label: 'SC Runtime',
      render: (r) => r.runtimeVersion ? <code>{r.runtimeVersion}</code> : '—' },
    // Installed bootstrap (sc-app) version from the latest heartbeat.
    { label: 'SC App',
      render: (r) => r.bootstrapVersion ? <code>{r.bootstrapVersion}</code> : '—' },
    // Board self-identity (migration 0378) — decoded from the reported Pi
    // revision code + measured eMMC. `—` until a board-reporting runtime beats in.
    { label: 'Board',
      render: (r) => {
        if (!r.boardModelName && !r.boardFamily) return '—';
        const model = r.boardModelName ?? (r.boardFamily ? r.boardFamily.toUpperCase() : '—');
        const ram = r.boardRamMb ? ` · ${Math.round(r.boardRamMb / 1024)}GB RAM` : '';
        const emmc = r.emmcTotalBytes ? ` · ${Math.round(r.emmcTotalBytes / 1e9)}GB eMMC` : '';
        return <span>{model}{ram}{emmc}</span>;
      } },
    { label: 'Board code',
      render: (r) => r.boardRevisionCode ? <code>{r.boardRevisionCode}</code> : '—' },
    { label: 'Last heartbeat',  render: (r) => r.lastHeartbeatAt  ? fmtDateTime(r.lastHeartbeatAt)  : '—' },
    { label: 'Cert expiry',     render: (r) => <CertExpiryCell expiry={r.deviceCertExpiry} /> },
    // SC SSH Phase-1 group F — cert lifecycle flags. An em-dash "healthy"
    // marker when neither flag is set so the row never renders blank.
    { label: 'Cert lifecycle',
      render: (r) => (r.certRenewalOverdue || r.certExpiredOffline)
        ? <CertLifecycleChips renewalOverdue={r.certRenewalOverdue} expiredOffline={r.certExpiredOffline} />
        : <Typography variant="body2" color="text.secondary">— (healthy)</Typography> },
    // SC SSH Phase-1 group H — break-glass console-login alarm. Only fetched +
    // shown for console-bg holders (the component self-gates on the permission).
    { label: 'Break-glass console access',
      separator: true,
      fullWidth: true,
      render: (r) => <BreakGlassAlert siteControllerId={r.id} canView={has('provisioning.site-controllers.console-bg')} /> },
    { label: 'Commissioned at', render: (r) => r.commissionedAt   ? fmtDateTime(r.commissionedAt)   : '—' },
    { label: 'Commissioning tech', render: (r) => fmtOrEmDash(r.commissioningTechName) },
    { label: 'Voided at',       render: (r) => r.voidedAt ? fmtDateTime(r.voidedAt) : '—' },
    { label: 'Void reason',     render: (r) => fmtOrEmDash(r.voidReason) },
    { label: 'Pending void requested at',
      render: (r) => r.pendingVoidRequestedAt ? fmtDateTime(r.pendingVoidRequestedAt) : '—' },
    // Stage 3 §M-T8.5 — heartbeat time-series panel + tabular subform.
    // fullWidth so the chart + table aren't indented by the label column.
    { label: 'Heartbeat',
      separator: true,
      fullWidth: true,
      render: (r) => <SiteControllerHeartbeatPanel siteControllerId={r.id} /> },
    ...auditTrailFields<SiteControllerSummary>(),
  ], [operatorById, has]);

  if (!has('provisioning.site-controllers.view')) {
    return (
      <>
        <Typography color="text.secondary">
          You don't have permission to view site controllers.
        </Typography>
      </>
    );
  }

  return (
    <>
      <Stack spacing={2}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
          <Typography variant="h4" sx={{ flex: 1 }}>Site controllers</Typography>
          {isArms && (
            <TextField
              select
              size="small"
              label="Operator"
              value={operatorFilter}
              onChange={(e) => setOperatorFilter(e.target.value)}
              sx={{ minWidth: 240 }}
            >
              <MenuItem value="">All operators (incl. uncommissioned pool)</MenuItem>
              {(operatorsQ.data ?? []).map((op) => (
                <MenuItem key={op.id} value={op.id}>
                  {op.displayName} ({op.code})
                </MenuItem>
              ))}
            </TextField>
          )}
        </Stack>

        <RouteDataGrid<SiteControllerSummary>
          entityName="Site controllers"
          queryKey={queryKey}
          listFn={listFn}
          columns={columns}
          searchFields={searchFields}
          quickFilters={quickFilters}
          exportColumns={exportColumns}
          reportTitle="ARMS — Site controllers"
          actions={actions}
          onRowClick={(row) => setDetailRow(row)}
          emptyMessage={isArms && !operatorFilter
            ? 'No site controllers registered yet.'
            : 'No site controllers for this operator yet.'}
        />

        <EntityDetailDialog<SiteControllerSummary>
          open={detailRow !== null}
          title={detailRow?.siteDisplayName ?? 'Site controller'}
          data={detailRow}
          fields={detailFields}
          onClose={() => setDetailRow(null)}
          // Stage 3 §M-T8.5 — wider so the heartbeat Recharts panel fits.
          maxWidth="lg"
        />

        <VoidRequestDialog
          row={voidTarget}
          onClose={() => setVoidTarget(null)}
          mode="void"
        />
        <VoidApproveDialog
          row={approveVoidTarget}
          onClose={() => setApproveVoidTarget(null)}
          mode="void"
        />
        <VoidRequestDialog
          row={reverseVoidTarget}
          onClose={() => setReverseVoidTarget(null)}
          mode="reversal"
        />
        <VoidApproveDialog
          row={approveReversalTarget}
          onClose={() => setApproveReversalTarget(null)}
          mode="reversal"
        />

        <LifecycleActionDialog
          target={lifecycleTarget}
          onClose={() => setLifecycleTarget(null)}
        />

        <TerminalDialog
          row={terminalTarget}
          onClose={() => setTerminalTarget(null)}
        />
      </Stack>
    </>
  );
}

// ----- V2 §16 void / reversal dialogs -----

/**
 * Step 1: request a void or void-reversal with mandatory rationale text.
 * Per V2 §16.2 a separate ARMS-Staff user must approve before status
 * actually flips — see VoidApproveDialog.
 */
function VoidRequestDialog({
  row, onClose, mode,
}: {
  row: SiteControllerSummary | null;
  onClose: () => void;
  mode: 'void' | 'reversal';
}) {
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const requestVoid    = useRequestVoid(row?.id ?? '00000000-0000-0000-0000-000000000000');
  const requestReverse = useRequestVoidReversal(row?.id ?? '00000000-0000-0000-0000-000000000000');
  const mut = mode === 'void' ? requestVoid : requestReverse;

  const close = () => {
    if (mut.isPending) return;
    setReason(''); setError(null);
    onClose();
  };

  const submit = () => {
    setError(null);
    if (!reason.trim()) {
      setError('Reason is required.');
      return;
    }
    mut.mutate(
      { reason: reason.trim() },
      {
        onSuccess: close,
        onError:   (e) => setError((e as { message?: string }).message ?? 'Request failed.'),
      },
    );
  };

  const title = mode === 'void' ? 'Request void' : 'Request void reversal';
  const description = mode === 'void'
    ? 'Mark the site controller for voiding. The action will not commit until another authorized user approves.'
    : 'Reverse the previous void. The reversal will not commit until another authorized user approves.';
  const submitLabel = mode === 'void' ? 'Request void' : 'Request reversal';

  return (
    <Dialog open={row !== null} onClose={close} fullWidth maxWidth="sm">
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {description}
          </Typography>
          {row && (
            <Typography variant="body2">
              Site controller: <code>{row.qrId ?? row.id}</code> · {row.status.replace('_', ' ')}
            </Typography>
          )}
          <TextField label="Reason (required, audited)" value={reason}
                     onChange={(e) => setReason(e.target.value)}
                     disabled={mut.isPending} fullWidth multiline rows={3}
                     autoFocus />
          {error && <Typography color="error" variant="body2">{error}</Typography>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={close} disabled={mut.isPending}>Cancel</Button>
        <Button onClick={submit} disabled={mut.isPending}
                variant="contained"
                color={mode === 'void' ? 'error' : 'primary'}>
          {mut.isPending ? 'Submitting…' : submitLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * Step 2: a different ARMS-Staff user approves the pending void or
 * reversal. Self-approval is enforced server-side (V2 §16.2).
 */
function VoidApproveDialog({
  row, onClose, mode,
}: {
  row: SiteControllerSummary | null;
  onClose: () => void;
  mode: 'void' | 'reversal';
}) {
  const [comment, setComment] = useState('');
  const [error, setError] = useState<string | null>(null);
  const approveVoid    = useApproveVoid(row?.id ?? '00000000-0000-0000-0000-000000000000');
  const approveReverse = useApproveVoidReversal(row?.id ?? '00000000-0000-0000-0000-000000000000');
  const cancelVoid     = useCancelPendingVoid(row?.id ?? '00000000-0000-0000-0000-000000000000');
  const cancelReverse  = useCancelPendingVoidReversal(row?.id ?? '00000000-0000-0000-0000-000000000000');
  const approveMut = mode === 'void' ? approveVoid : approveReverse;
  const cancelMut  = mode === 'void' ? cancelVoid  : cancelReverse;

  const busy = approveMut.isPending || cancelMut.isPending;

  const close = () => {
    if (busy) return;
    setComment(''); setError(null);
    onClose();
  };

  const submitApprove = () => {
    setError(null);
    approveMut.mutate(
      { comment: comment.trim() || null },
      {
        onSuccess: close,
        onError:   (e) => setError((e as { message?: string }).message ?? 'Approve failed.'),
      },
    );
  };

  const submitCancel = () => {
    setError(null);
    cancelMut.mutate(undefined, {
      onSuccess: close,
      onError:   (e) => setError((e as { message?: string }).message ?? 'Cancel failed.'),
    });
  };

  const title = mode === 'void' ? 'Approve void' : 'Approve void reversal';

  return (
    <Dialog open={row !== null} onClose={close} fullWidth maxWidth="sm">
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Confirm the pending {mode === 'void' ? 'void' : 'reversal'}. The
            request was made by another user; self-approval is
            blocked. Once approved the controller's status flips and the
            action becomes part of the audit trail.
          </Typography>
          {row && (
            <Typography variant="body2">
              Site controller: <code>{row.qrId ?? row.id}</code>
              {row.voidReason ? ` · reason: ${row.voidReason}` : ''}
            </Typography>
          )}
          <TextField label="Approver comment (optional, audited)" value={comment}
                     onChange={(e) => setComment(e.target.value)}
                     disabled={busy} fullWidth multiline rows={2} />
          {error && <Typography color="error" variant="body2">{error}</Typography>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={submitCancel} disabled={busy} color="inherit">
          {cancelMut.isPending ? 'Cancelling…' : `Cancel pending ${mode === 'void' ? 'void' : 'reversal'}`}
        </Button>
        <Button onClick={close} disabled={busy}>Close</Button>
        <Button onClick={submitApprove} disabled={busy}
                variant="contained"
                color={mode === 'void' ? 'error' : 'primary'}>
          {approveMut.isPending ? 'Approving…' : 'Approve'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function LifecycleActionDialog({
  target, onClose,
}: {
  target: { sc: SiteControllerSummary; action: 'decommission' | 'recommission' } | null;
  onClose: () => void;
}) {
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const lifecycle = useScLifecycleAction(target?.sc.id ?? '00000000-0000-0000-0000-000000000000');

  const close = () => {
    if (lifecycle.isPending) return;
    setReason(''); setError(null);
    onClose();
  };

  const submit = () => {
    setError(null);
    if (!target) return;
    lifecycle.mutate(
      { action: target.action, reason: reason.trim() || null },
      {
        onSuccess: close,
        onError:   (e) => setError((e as { message?: string }).message ?? 'Action failed.'),
      },
    );
  };

  const titles = {
    decommission: 'Decommission site controller',
    recommission: 'Recommission site controller',
  };
  const descriptions = {
    decommission: "Mark the site controller for teardown. mTLS continues until cert expires; physical removal is the operator's next step.",
    recommission: 'Bring the device back into the lifecycle. State returns to ready_for_install — operator + site bindings are cleared so the device can be commissioned at a fresh site.',
  };
  const destructive = target?.action === 'decommission';

  return (
    <Dialog open={target !== null} onClose={close} fullWidth maxWidth="sm">
      <DialogTitle>{target ? titles[target.action] : 'Lifecycle action'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {target && (
            <>
              <Typography variant="body2" color="text.secondary">
                {descriptions[target.action]}
              </Typography>
              <Typography variant="body2">
                Site controller: <code>{target.sc.qrId ?? target.sc.id}</code> · {target.sc.status.replace('_', ' ')}
                {target.sc.siteDisplayName ? ` · ${target.sc.siteDisplayName}` : ''}
              </Typography>
            </>
          )}
          <TextField label="Reason (optional, audited)" value={reason}
                     onChange={(e) => setReason(e.target.value)}
                     disabled={lifecycle.isPending} fullWidth multiline rows={2}
                     autoFocus />
          {error && <Typography color="error" variant="body2">{error}</Typography>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={close} disabled={lifecycle.isPending}>Cancel</Button>
        <Button onClick={submit} disabled={lifecycle.isPending}
                variant="contained"
                color={destructive ? 'error' : 'primary'}>
          {lifecycle.isPending ? 'Submitting…' : (target ? titles[target.action].split(' ')[0] : 'Confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// V2 status set per Site_Controller_Commissioning_Plan_V2.docx §19.
const STATUS_OPTIONS = [
  { value: 'ready_for_install',     label: 'Ready for install (uncommissioned)' },
  { value: 'commissioning',         label: 'Commissioning' },
  { value: 'installing',            label: 'Installing' },
  { value: 'in_service',            label: 'In service' },
  { value: 'voided',                label: 'Voided' },
  { value: 'defective',             label: 'Defective' },
  { value: 'decommissioning',       label: 'Decommissioning' },
  { value: 'retired',               label: 'Retired' },
];

/**
 * Cert expiry display with graduated severity:
 *   - more than 21 days out → plain text date
 *   - 14–21 days            → info chip
 *   - 7–14 days             → warning chip
 *   - less than 7 days      → error chip
 *   - already expired       → error chip "expired"
 *
 * No cert yet (null) renders an em-dash so the column stays aligned.
 */
function CertExpiryCell({ expiry }: { expiry: string | null }) {
  if (!expiry) {
    return <Typography variant="body2" color="text.secondary">—</Typography>;
  }
  const expiresAt = new Date(expiry);
  const daysLeft  = Math.floor((expiresAt.getTime() - Date.now()) / 86_400_000);
  const dateText  = fmtDateTime(expiry);

  if (daysLeft < 0) {
    return <Chip size="small" label={`expired ${-daysLeft}d ago`} color="error" />;
  }
  if (daysLeft < 7) {
    return <Chip size="small" label={`${dateText} (${daysLeft}d left)`} color="error" />;
  }
  if (daysLeft < 14) {
    return <Chip size="small" label={`${dateText} (${daysLeft}d left)`} color="warning" />;
  }
  if (daysLeft < 21) {
    return <Chip size="small" label={`${dateText} (${daysLeft}d left)`} color="info" />;
  }
  return <Typography variant="body2">{dateText}</Typography>;
}

/**
 * SC SSH Phase-1 group F — cert lifecycle warning chips. Renders nothing when
 * neither flag is set (the caller decides the "healthy" affordance), so it can
 * be dropped into the Status column without leaving an empty gap.
 */
function CertLifecycleChips({
  renewalOverdue, expiredOffline,
}: {
  renewalOverdue: boolean;
  expiredOffline: boolean;
}) {
  return (
    <>
      {renewalOverdue && <Chip size="small" label="cert renewal overdue" color="warning" />}
      {expiredOffline && <Chip size="small" label="cert expired (offline)" color="error" />}
    </>
  );
}

/**
 * SC SSH Phase-1 group H — break-glass console-login alarm panel for the SC
 * detail. Self-gates on `canView` (the console-bg permission) so non-holders
 * never trigger a 403; for holders it loads the alarm feed and renders a loud
 * error Alert listing the most-recent physical-console arms-admin logins, or a
 * quiet "none recorded" line when the SC has had no break-glass access.
 */
function BreakGlassAlert({
  siteControllerId, canView,
}: {
  siteControllerId: string;
  canView: boolean;
}) {
  const feed = useConsoleBreakGlass(siteControllerId, canView);

  if (!canView) {
    return (
      <Typography variant="body2" color="text.secondary">
        — (requires the console break-glass permission)
      </Typography>
    );
  }
  if (feed.isLoading) {
    return <Typography variant="body2" color="text.secondary">Loading break-glass history…</Typography>;
  }
  if (feed.isError) {
    return (
      <Alert severity="error" variant="outlined">
        Failed to load break-glass console-access history
        {(feed.error as { message?: string })?.message ? `: ${(feed.error as { message?: string }).message}` : '.'}
      </Alert>
    );
  }
  const alarms = feed.data?.alarms ?? [];
  if (alarms.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No physical-console break-glass logins recorded.
      </Typography>
    );
  }
  return (
    <Alert severity="error" variant="filled" data-testid="break-glass-alert">
      <AlertTitle>
        Break-glass console access used — {alarms.length} login{alarms.length === 1 ? '' : 's'}
      </AlertTitle>
      <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
        {alarms.map((a) => (
          <li key={a.id}>
            {a.loginAt ? fmtDateTime(a.loginAt) : fmtDateTime(a.occurredAt)}
            {' · '}{fmtOrEmDash(a.account)}
            {' @ '}{fmtOrEmDash(a.terminal)}
            {a.loginOutcome ? ` · ${a.loginOutcome}` : ''}
          </li>
        ))}
      </Box>
    </Alert>
  );
}

function StatusChip({ status }: { status: string }) {
  const color =
    status === 'in_service'                 ? 'success' :
    status === 'commissioning' ||
    status === 'installing'                 ? 'info' :
    status === 'ready_for_install'          ? 'default' :
    status === 'voided' ||
    status === 'defective' ||
    status === 'retired' ||
    status === 'decommissioning'            ? 'error' :
    'default';
  const label = STATUS_OPTIONS.find((o) => o.value === status)?.label ?? status;
  return <Chip size="small" label={label} color={color as 'success' | 'info' | 'error' | 'default'} />;
}
